import { Button, SelectOption } from "@clockwise/design-system";
import { Room } from "@clockwise/schema";
import { InputButton } from "@clockwise/web-commons/src/components/InputButton";
import { isValidUrl } from "@clockwise/web-commons/src/util/routes.util";
import { EditLocationRounded as EditLocationIcon, OpenInNew } from "@material-ui/icons";
import { find, map } from "lodash";
import React, { useMemo } from "react";
import { useBoolean } from "usehooks-ts";
import { Async } from "../atoms/Async";
import { EventElementsWrap } from "../atoms/EventElementsWrap";
import { SelectRoomActions } from "../atoms/SelectRoomActions";
import { SchemaRoomToRoomOption, SelectRooms } from "../atoms/SelectRooms";

type Props = {
  location?: string;
  onLocationChange: (location?: string) => void;
  onRoomSelect: (roomIds?: string) => void; // TODO: Select needs to support select multiple for this
  readonly?: boolean;
  rooms?: Room[];
  selectedRoomId?: string; // TODO: Select needs to support select multiple for this
  loading?: boolean;
  showChanges?: boolean;
};

export const ECRoomAndLocation = ({
  location = "",
  onLocationChange,
  onRoomSelect,
  readonly: disabled = false,
  rooms,
  selectedRoomId,
  loading = false,
  showChanges,
}: Props) => {
  const hasRoomSelection = !!selectedRoomId;
  const hasLocation = !!location;
  const { value: isOpen, setTrue: setOpen } = useBoolean(hasRoomSelection || hasLocation);

  const roomOptions = map(rooms, SchemaRoomToRoomOption);
  const selectedRoom = find(rooms, (room) => selectedRoomId === room.id);

  const handleRemoveRoom = () => onRoomSelect(undefined);

  const canAddMeetingRooms = rooms && rooms.length >= 1;

  const routeAction = useMemo(() => {
    if (isValidUrl(location)) {
      return () => window.open(location, "_blank");
    } else {
      return null;
    }
  }, [location]);

  let body = null;

  if (disabled) {
    if (routeAction) {
      body = (
        <Button variant="text" onClick={routeAction} size="xsmall">
          {location}
        </Button>
      );
    } else {
      body = (
        <span className="cw-body-sm cw-font-medium cw-ml-[5px]">{location || "No location"}</span>
      );
    }
  } else if (!isOpen && canAddMeetingRooms) {
    body = (
      <Button variant="text" onClick={setOpen} size="xsmall" disabled={disabled}>
        Add room or location
      </Button>
    );
  } else {
    body = (
      <div className="cw-flex cw-flex-col cw-grow cw-space-y-1 cw-truncate">
        <SelectRooms
          disabled={disabled}
          onSelect={onRoomSelect}
          options={roomOptions}
          selected={selectedRoomId}
        />
        <SelectRoomActions
          disabled={disabled}
          selected={selectedRoom}
          onRemove={handleRemoveRoom}
        />
        <InputButton
          disabled={disabled}
          editIcon={EditLocationIcon}
          onChange={onLocationChange}
          customActions={
            routeAction
              ? [
                  <SelectOption
                    key="routeActionKey"
                    value={routeAction}
                    icon={OpenInNew}
                    iconProps={{ className: "cw-text-muted" }}
                  >
                    Visit Link
                  </SelectOption>,
                ]
              : []
          }
          placeholder="Add location"
          value={location}
        />
      </div>
    );
  }

  return (
    <EventElementsWrap name="location" showChanges={showChanges}>
      <Async loading={loading} loadingHeight="28px" loadingWidth="100px">
        {body}
      </Async>
    </EventElementsWrap>
  );
};
